import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { DateRangePicker, SingleDatePicker } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';
import {
  getApplyFilter,
  tableNameInLocalStorage,
} from '../../utils/table-functions';
import BorderSelect from '../border-select/border-select-component';
import Style from '../border-select/border-select-component-style';
import DateTimeRangePicker from '../date-time-range-picker/date-time-range-picker-component';
import Button from '../table-button/table-button-component';
import Checkbox from '../table-checkbox/table-checkbox-component';
import SearchInput from '../table-search-Input/table-search-input-component';

import { Col, Row } from '../grid/grid-component';
import { H2 } from '../typography/typography-component';
import Styled from './table-header-style';

/**
 * Removes duplicates from an array of objects.
 * @param {Array} list - The list to remove duplicates from.
 * @returns {Array} The list without duplicates.
 */
const removeDuplicates = (list) => {
  const flag = {};
  const unique = [];
  list.forEach((element) => {
    if (!flag[element.id]) {
      flag[element.id] = true;
      unique.push(element);
    }
  });
  return unique;
};

const years = Array.from({ length: 5 }, (v, i) => dayjs().year() - 5 + i + 1);
const styledCol = { height: 45 };
/**
 * Table header component for generic use in Table component.
 * @param {object} props - Props of the component.
 * @param {Array} props.filters - Filters to display in the table.
 * @param {object} props.variables - Variables of the table.
 * @param {Function} props.setVariables - Function to set the variables of the table.
 * @param {Function} props.setHiddenColumns - Function to set the hidden columns of the table.
 * @param {Array} props.tableOptions - Options of the table.
 * @param {object} props.defaultVariables - Default variables of the table.
 * @param {Array} props.columns - Columns of the table.
 * @param {Array} props.visibleColumns - Visible columns of the table.
 * @param {Function} props.setVisibleColumns - Function to set the visible columns of the table.
 * @param {Function} props.download - Function to download the table.
 * @param {Function} props.customClear - Function to clear the table.
 * @param {string} props.id - ID of the table.
 * @param {JSX.Element} props.tableHeaderProps Elements added to header
 * @param {Array} props.additionalHeaderButtons - Additional header buttons of the table.
 * @param {boolean} props.clearDefaultOnReset - Flag if the default variables should be cleared on reset.
 * @returns {JSX.Element}
 */
const TableHeaderComponent = ({
  filters,
  variables,
  setVariables,
  setHiddenColumns,
  tableOptions,
  defaultVariables,
  columns,
  visibleColumns,
  setVisibleColumns,
  download,
  customClear,
  tableHeaderProps,
  id: tableId,
  additionalHeaderButtons,
  clearDefaultOnReset,
}) => {
  if (!tableId) {
    throw new Error('Table ID not provided');
  }
  const [filterValues, setFilterValues] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [columnOpen, setColumnOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(variables?.search || '');
  const [initLoad, setInitLoad] = useState(true);

  /**
   * Clears the search.
   * @returns {void}
   */
  const clearSearch = () => {
    const variablesCopy = JSON.parse(JSON.stringify(variables));
    delete variablesCopy.search;
    setTimeout(() => {
      setSearchValue('');
    }, 100);
    setVariables(variablesCopy);
  };

  /**
   * Toggles the visibility of a column.
   * @param {object} e - The event object.
   * @param {string} id - The ID of the column.
   * @returns {void}
   */
  const toggleVisible = (e, id) => {
    let tempVisibleColumns = visibleColumns.map((col) => ({ ...col }));
    /**
     *
     */
    const toggleCheckMark = () => {
      tempVisibleColumns.forEach((col, i) => {
        if (col.id === id) {
          tempVisibleColumns[i].visible = e.target.checked;
        }
      });
    };

    const tablePreferences = localStorage.getItem('tablePreferences');
    const preferences = tablePreferences && JSON.parse(tablePreferences);
    if (preferences && tableNameInLocalStorage(preferences, tableId)) {
      const { [tableId]: tableConfigs } = preferences;
      const { visibleColumns: visibleCols } = tableConfigs;

      const merged = visibleColumns?.map((col) => {
        if (visibleCols?.length > 0) {
          const found = visibleCols.find((visCol) => visCol.id === col.id);
          if (found) {
            return found;
          }
        }
        return col;
      });
      tempVisibleColumns = removeDuplicates(merged);
      toggleCheckMark();
      preferences[tableId] = {
        ...preferences[tableId],
        visibleColumns: tempVisibleColumns,
      };
      localStorage.setItem('tablePreferences', JSON.stringify(preferences));
    } else if (preferences && !tableNameInLocalStorage(preferences, tableId)) {
      tempVisibleColumns = visibleColumns.map((col) => ({ ...col }));
      toggleCheckMark();
      preferences[tableId] = {
        ...preferences[tableId],
        visibleColumns: tempVisibleColumns,
      };
      localStorage.setItem('tablePreferences', JSON.stringify(preferences));
    } else {
      tempVisibleColumns = visibleColumns.map((col) => ({ ...col }));
      toggleCheckMark();
      const item = JSON.stringify({
        [tableId]: {
          visibleColumns: tempVisibleColumns,
        },
      });
      localStorage.setItem('tablePreferences', item);
    }

    setVisibleColumns(tempVisibleColumns);
  };
  const createFilters = useCallback(
    (reset = false) => {
      setFilterValues((prevValue) => {
        let defaultFilters = defaultVariables.filter ?? {};
        if (JSON.stringify(prevValue) === '{}' || reset) {
          if (clearDefaultOnReset && reset) {
            defaultFilters = {};
          }

          const tempFilters = { ...prevValue };
          filters.forEach((filter) => {
            switch (filter.type) {
              case 'daterange':
              case 'datetimerange': {
                let filterValue = {
                  startDate: null,
                  endDate: null,
                  focusedInput: null,
                };

                if (defaultFilters[filter.accessor]) {
                  filterValue = defaultFilters[filter.accessor].value;
                }

                tempFilters[filter.accessor] = filterValue;
                break;
              }
              case 'date': {
                let filterValue = {
                  date: null,
                  focusedInput: { focused: null },
                };

                if (defaultFilters[filter.accessor]) {
                  filterValue = defaultFilters[filter.accessor].value;
                }

                tempFilters[filter.accessor] = filterValue;
                break;
              }
              case 'select':
              case 'multiselect': {
                let filterValue = '';

                if (defaultFilters[filter.accessor]) {
                  filterValue = defaultFilters[filter.accessor].value;
                }

                tempFilters[filter.accessor] = {
                  value: filterValue,
                };
                break;
              }
              default:
                break;
            }
          });
          if (reset) {
            if (defaultFilters) {
              setVariables({ filter: defaultFilters });
            }
          }
          return tempFilters;
        }
        return prevValue;
      });
    },
    [defaultVariables.filter, clearDefaultOnReset, filters, setVariables],
  );
  const handleDatesChange = useCallback(
    (startDate, endDate, accessor) => {
      setFilterValues((prevVal) => {
        const tempFilters = prevVal;

        tempFilters[accessor] = {
          ...tempFilters[accessor],
          startDate: startDate
            ? dayjs(startDate.utcOffset(0).toISOString()).utcOffset(0)
            : null,
          endDate: endDate
            ? dayjs(endDate.utcOffset(0).toISOString()).utcOffset(0)
            : null,
        };
        return tempFilters;
      });
    },
    [setFilterValues],
  );
  const handleDateChange = useCallback(
    (date, accessor) => {
      setFilterValues((prevVal) => {
        const tempFilters = prevVal;

        tempFilters[accessor] = {
          ...tempFilters[accessor],
          date: date
            ? dayjs(date.utcOffset(0).toISOString()).utcOffset(0)
            : null,
        };
        return tempFilters;
      });
    },
    [setFilterValues],
  );
  const handleTimeChange = useCallback(
    (date, dateType, accessor) => {
      setFilterValues((prevVal) => {
        const tempFilters = { ...prevVal };
        let fUpdate = 0;

        if (tempFilters[accessor].fUpdate) {
          // eslint-disable-next-line prefer-destructuring
          fUpdate = tempFilters[accessor].fUpdate;
        }

        if (dateType === 'startDate') {
          tempFilters[accessor] = {
            ...tempFilters[accessor],
            startDate: date
              ? dayjs(date.utcOffset(0).toISOString()).utcOffset(0)
              : null,
            fUpdate: fUpdate + 1,
          };
        } else {
          tempFilters[accessor] = {
            ...tempFilters[accessor],
            endDate: date
              ? dayjs(date.utcOffset(0).toISOString()).utcOffset(0)
              : null,
            fUpdate: fUpdate + 1,
          };
        }
        return tempFilters;
      });
    },
    [setFilterValues],
  );

  const handleFocusChange = useCallback(
    (focusedInput, accessor) => {
      setFilterValues((prevVal) => {
        const tempFilters = { ...prevVal };

        tempFilters[accessor] = {
          ...tempFilters[accessor],
          focusedInput,
        };
        return tempFilters;
      });
    },
    [setFilterValues],
  );
  const handleChangeSelect = useCallback(
    (e, accessor, cb) => {
      setFilterValues((prevVal) => {
        const tempFilters = { ...prevVal };
        tempFilters[accessor] = {
          value: e.target.value,
        };
        if (cb) {
          cb(e.target.value);
        }
        return tempFilters;
      });
    },
    [setFilterValues],
  );

  const handleChangeMultiSelect = useCallback(
    (val, accessor) => {
      setFilterValues((prevValue) => {
        const tempFilters = JSON.parse(JSON.stringify(prevValue));
        tempFilters[accessor] = {
          value: val,
        };
        return tempFilters;
      });
    },
    [setFilterValues],
  );

  /**
   *
   */
  const clearFilters = () => {
    createFilters(true);
    if (customClear) {
      customClear();
    }
  };
  const applyFilters = useCallback(() => {
    setVariables((prevValue) => {
      const newFilters = getApplyFilter(
        filterValues,
        filters,
        { ...prevValue },
        clearDefaultOnReset ? {} : defaultVariables,
      );
      if (newFilters) {
        setFilterOpen(false);

        const tablePreferences = localStorage.getItem('tablePreferences');
        const preferences = tablePreferences && JSON.parse(tablePreferences);

        Object.keys(newFilters.variables.filter).forEach((key) => {
          const filterValue = `${key}${newFilters.variables.filter[key].type}=${newFilters.variables.filter[key].value}`;
          preferences[tableId] = {
            ...preferences[tableId],
            query: [...preferences[tableId].query, `filter=${filterValue}`],
          };
        });

        localStorage.setItem('tablePreferences', JSON.stringify(preferences));
        return newFilters.variables;
      }
      setFilterOpen(false);
      return prevValue;
    });
  }, [
    setVariables,
    filterValues,
    filters,
    clearDefaultOnReset,
    defaultVariables,
    tableId,
  ]);

  /**
   * Renders the filter option.
   * @returns {JSX.Element}
   */
  const renderFilterOption = () => (
    <Styled.DropDownHolder key="filter">
      <Styled.Section onClick={() => setFilterOpen(!filterOpen)}>
        <Styled.FilterListIcon
          className={filterOpen ? 'active' : ''}
          data-tip="Filter"
        />
        Filter
      </Styled.Section>
      {filterOpen && (
        <Styled.DropDownDismiss
          onClick={() => {
            setFilterOpen(false);
          }}
        />
      )}
      <Styled.DropDown open={filterOpen}>
        <Styled.DropDownContent>
          <Styled.TableFiltersDiv className="title">
            Table Filters:
          </Styled.TableFiltersDiv>
          <Row className="body">
            {filters.map((filter) => {
              switch (filter.type) {
                case 'daterange':
                  if (filterValues[filter.accessor]) {
                    return (
                      <Col key={uuidv4()} md={12} lg={6}>
                        <H2>{filter.header}</H2>
                        <DateRangePicker
                          startDate={filterValues[filter.accessor].startDate} // dayjsPropTypes.dayjsObj or null,
                          startDateId={`start_date_${filter.accessor}`} // PropTypes.string.isRequired,
                          endDate={filterValues[filter.accessor].endDate} // dayjsPropTypes.dayjsObj or null,
                          endDateId={`end_date_${filter.accessor}`} // PropTypes.string.isRequired,
                          onFocusChange={(e) => {
                            handleFocusChange(e, filter.accessor);
                          }}
                          onDatesChange={({ startDate, endDate }) => {
                            handleDatesChange(
                              startDate,
                              endDate,
                              filter.accessor,
                            );
                          }}
                          focusedInput={
                            filterValues[filter.accessor].focusedInput
                          }
                          block={false}
                          numberOfMonths={2}
                          displayFormat="YYYY/MM/DD"
                          orientation="horizontal"
                          anchorDirection="left"
                          isOutsideRange={() => false}
                          withPortal
                        />
                      </Col>
                    );
                  }
                  return null;
                case 'date':
                  if (filterValues[filter.accessor]) {
                    return (
                      <Col key={uuidv4()} md={12} lg={6}>
                        <H2>{filter.header}</H2>
                        <SingleDatePicker
                          date={filterValues[filter.accessor].date} // dayjsPropTypes.dayjsObj or null,
                          id={`${filter.accessor}`} // PropTypes.string.isRequired,
                          onFocusChange={(e) => {
                            if (
                              filterValues[filter.accessor].focusedInput
                                .focused !== e.focused
                            ) {
                              handleFocusChange(e, filter.accessor);
                            }
                          }}
                          onDateChange={(date) => {
                            handleDateChange(date, filter.accessor);
                          }}
                          block
                          withPortal
                          numberOfMonths={1}
                          focused={
                            filterValues[filter.accessor].focusedInput.focused
                          }
                          displayFormat="YYYY/MM/DD"
                          isOutsideRange={() => false}
                          orientation="horizontal"
                          renderMonthElement={({
                            month,
                            onMonthSelect,
                            onYearSelect,
                          }) => (
                            <Styled.DivDropdown>
                              <Style.DateDropDown>
                                <BorderSelect
                                  label="Month"
                                  value={month.format('MMMM')}
                                  onChange={(e) => {
                                    onMonthSelect(month, e.target.value);
                                  }}
                                  options={dayjs
                                    .months()
                                    .map((value) => ({ value, text: value }))}
                                />
                                <BorderSelect
                                  label="Year"
                                  value={month.format('yyyy')}
                                  onChange={(e) => {
                                    onYearSelect(month, e.target.value);
                                  }}
                                  options={years.map((value) => ({
                                    value,
                                    text: value,
                                  }))}
                                />
                              </Style.DateDropDown>
                            </Styled.DivDropdown>
                          )}
                        />
                      </Col>
                    );
                  }
                  return null;
                case 'datetimerange':
                  if (filterValues[filter.accessor]) {
                    return (
                      <Col key={uuidv4()} md={12} lg={6}>
                        <H2>{filter.header}</H2>
                        <DateTimeRangePicker
                          startDate={filterValues[filter.accessor].startDate} // dayjsPropTypes.dayjsObj or null,
                          startDateId={`start_date_${filter.accessor}`} // PropTypes.string.isRequired,
                          endDate={filterValues[filter.accessor].endDate} // dayjsPropTypes.dayjsObj or null,
                          endDateId={`end_date_${filter.accessor}`} // PropTypes.string.isRequired,
                          handleFocusChange={(e) => {
                            handleFocusChange(e, filter.accessor);
                          }}
                          handleDatesChange={({ startDate, endDate }) => {
                            handleDatesChange(
                              startDate,
                              endDate,
                              filter.accessor,
                            );
                          }}
                          focusedInput={
                            filterValues[filter.accessor].focusedInput
                          }
                          block={false}
                          numberOfMonths={2}
                          handleTimeChange={(d, t) => {
                            handleTimeChange(d, t, filter.accessor);
                          }}
                          displayFormat="YYYY/MM/DD"
                          orientation="horizontal"
                          anchorDirection="left"
                          isOutsideRange={() => false}
                        />
                      </Col>
                    );
                  }
                  return null;

                case 'select':
                  if (filterValues[filter.accessor]) {
                    return (
                      <Col key={uuidv4()} md={12} lg={6}>
                        <H2>{filter.header}</H2>
                        <BorderSelect
                          name="status"
                          label={filter.placeholder}
                          value={filterValues[filter.accessor].value}
                          onChange={(e) => {
                            handleChangeSelect(e, filter.accessor, (val) => {
                              if (filter.setSelected) {
                                filter.setSelected(val);
                              }
                            });
                          }}
                          options={filter.options}
                        />
                      </Col>
                    );
                  }
                  return null;
                case 'multiselect':
                  if (filterValues[filter.accessor]) {
                    return (
                      <Col key={uuidv4()} md={12} lg={6}>
                        <H2>{filter.header}</H2>
                        <BorderSelect
                          isMulti
                          name="status"
                          label={filter.placeholder}
                          value={filterValues[filter.accessor].value}
                          onChange={(e) => {
                            handleChangeMultiSelect(
                              e,
                              filter.accessor,
                              (val) => {
                                if (filter.setSelected) {
                                  filter.setSelected(val);
                                }
                              },
                            );
                          }}
                          options={filter.options}
                        />
                      </Col>
                    );
                  }
                  return null;
                default:
                  return null;
              }
            })}
          </Row>
          <div className="footer">
            <Button
              onClick={applyFilters}
              kind="secondary"
              value="Filter"
              width="calc(50% - 5px)"
              margin="10px 5px 10px 0px"
            />
            <Button
              onClick={clearFilters}
              value="Clear Filters"
              width="calc(50% - 5px)"
              margin="10px 0 10px 5px"
            />
          </div>
        </Styled.DropDownContent>
      </Styled.DropDown>
    </Styled.DropDownHolder>
  );

  // USE EFFECTS
  useEffect(() => {
    if (!initLoad) {
      return;
    }
    // COLUMN DATA
    const tablePreferences = localStorage.getItem('tablePreferences');
    let tempVisibleColumns = [];
    if (tablePreferences) {
      const { [tableId]: tableConfigs } = JSON.parse(tablePreferences);
      const { visibleColumns: visibleCols } = tableConfigs || {};
      if (visibleCols) {
        const merged = visibleCols ? [...visibleCols, ...columns] : columns;
        tempVisibleColumns = removeDuplicates(merged);
      }

      const searchQuery = tableConfigs?.query?.filter((q) =>
        q.includes('search='),
      );
      let search;
      if (searchQuery?.length > 0) {
        const tempSearch = searchQuery[0].replace('search=', '');
        if (tempSearch !== variables.search) {
          search = tempSearch;
          setSearchValue(search);
        }
      }

      const filterQuery = tableConfigs?.query?.filter((q) =>
        q.includes('filter='),
      );

      if (filterQuery?.length > 0) {
        setFilterValues((prev) => {
          const newFilterValues = { ...prev };
          filterQuery.forEach((f) => {
            const tempFilter = f.replace('filter=', '');
            if (tempFilter.includes('$') && tempFilter.includes('=')) {
              const filterKey = tempFilter.split('$')[0];
              const filterValue = tempFilter.split('=')[1];
              const filterType = tempFilter.split('$')[1].split('=')[0];
              newFilterValues[filterKey] = {
                type: `$${filterType}`,
                value: filterValue,
              };
            }
          });
          return newFilterValues;
        });
      }
    }
    setVisibleColumns(() => {
      const tempColumns = [];
      columns.forEach((column, index) => {
        if (column.id) {
          const visible = tempVisibleColumns.length
            ? tempVisibleColumns[index].visible
            : column.visible || true;
          tempColumns.push({
            id: column.id,
            accessor: column.accessor,
            visible,
            title: column.Header,
          });
        }
      });
      return tempColumns;
    });

    // FILTER DATA
    createFilters();
    setInitLoad(false);
  }, [
    createFilters,
    defaultVariables,
    setVariables,
    setVisibleColumns,
    columns,
    tableId,
    variables,
    searchValue,
    initLoad,
  ]);

  /**
   * Renders the column option.
   * @returns {JSX.Element}
   */
  const renderColumnOption = () => (
    <Styled.DropDownHolder key="column">
      <Styled.Section onClick={() => setColumnOpen(!columnOpen)}>
        <Styled.SettingIcon
          className={columnOpen ? 'active' : ''}
          data-tip="Columns"
        />
        Columns
      </Styled.Section>
      {columnOpen && (
        <Styled.DropDownDismiss
          onClick={() => {
            setColumnOpen(false);
          }}
        />
      )}
      <Styled.DropDown open={columnOpen}>
        <Styled.DropDownContent>
          <Row>
            {visibleColumns.map(
              (col) =>
                col.title?.length > 0 && (
                  <Col
                    key={`checkbox-${uuidv4()}`}
                    md={12}
                    lg={6}
                    style={styledCol}
                  >
                    <Checkbox
                      type="checkbox"
                      width="100%"
                      margin="10px 0"
                      value={col.visible}
                      label={col.title}
                      onChange={(e) => {
                        toggleVisible(e, col.id);
                      }}
                    />
                  </Col>
                ),
            )}
          </Row>
        </Styled.DropDownContent>
      </Styled.DropDown>
    </Styled.DropDownHolder>
  );

  /**
   * Renders the options.
   * @returns {JSX.Element}
   */
  const renderOptions = () =>
    tableOptions.map((option) => {
      switch (option) {
        case 'columns':
          return renderColumnOption();
        case 'filter':
          return renderFilterOption();
        case 'download': {
          if (window.innerWidth < 1100) {
            return null;
          }
          return (
            <Styled.Section onClick={download}>
              <Styled.ArrowDownIcon data-tip="Download" key={uuidv4()} />
              Export
            </Styled.Section>
          );
        }
        default:
          return null;
      }
    });

  const renderAdditionalHeaderButtons = () =>
    additionalHeaderButtons.map((button) => {
      const ButtonIcon = button.icon;
      return (
        <Styled.Section onClick={button.onClick} key={uuidv4()}>
          {ButtonIcon && <ButtonIcon data-tip={button.title} />}
          {button.title}
        </Styled.Section>
      );
    });

  const debounced = useDebouncedCallback((search) => {
    if (search === '') {
      clearSearch();
    } else {
      setVariables({ ...variables, search });
    }
  }, 800);
  useEffect(() => {
    const hideColumns = [];
    visibleColumns.forEach((col) => {
      if (!col.visible) {
        hideColumns.push(col.id);
      }
    });
    setHiddenColumns(hideColumns);
  }, [visibleColumns, setHiddenColumns]);

  return (
    <Styled.TableHeader>
      <Styled.TableTitle>
        <SearchInput
          name="search"
          type="text"
          label="Search"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            debounced(e.target.value);
          }}
          customStyle={{ margin: '0' }}
          inputStyle={{ padding: '8px' }}
          id="searchBox"
          icon={
            <Styled.CloseIcon
              name="close-search"
              display={searchValue.length > 0 ? 'block' : 'none'}
              onClick={() => {
                clearSearch();
              }}
            />
          }
        />
      </Styled.TableTitle>
      <Styled.TableFilterIcons>
        {additionalHeaderButtons && renderAdditionalHeaderButtons()}
        {renderOptions()}
        {tableHeaderProps ?? tableHeaderProps}
      </Styled.TableFilterIcons>
    </Styled.TableHeader>
  );
};

TableHeaderComponent.defaultProps = {
  defaultVariables: {},
  download: undefined,
  customClear: undefined,
  tableHeaderProps: undefined,
  additionalHeaderButtons: undefined,
  clearDefaultOnReset: false,
};

TableHeaderComponent.propTypes = {
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  variables: PropTypes.shape({
    search: PropTypes.string,
    filter: PropTypes.shape({}),
  }).isRequired,
  setVariables: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  setHiddenColumns: PropTypes.func.isRequired,
  tableOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultVariables: PropTypes.shape({
    filter: PropTypes.shape({}),
  }),
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  visibleColumns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setVisibleColumns: PropTypes.func.isRequired,
  download: PropTypes.func,
  tableHeaderProps: PropTypes.element,
  customClear: PropTypes.func,
  additionalHeaderButtons: PropTypes.arrayOf(PropTypes.shape({})),
  clearDefaultOnReset: PropTypes.bool,
};

export default TableHeaderComponent;
